import { useRef, useState } from 'react';

export type GroupField = 'buildingName' | 'levelName' | 'room' | 'country' | 'state' | 'city';

const FIELD_LABELS: Record<GroupField, string> = {
  country: 'Country',
  state: 'State',
  city: 'City',
  buildingName: 'Building',
  levelName: 'Level',
  room: 'Room',
};

const ALL_FIELDS: GroupField[] = ['country', 'state', 'city', 'buildingName', 'levelName', 'room'];

interface GroupByBarProps {
  active: GroupField[];
  onChange: (fields: GroupField[]) => void;
}

export function GroupByBar({ active, onChange }: GroupByBarProps) {
  const available = ALL_FIELDS.filter((f) => !active.includes(f));
  const dragIndex = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  function addField(field: GroupField) {
    onChange([...active, field]);
  }

  function removeField(field: GroupField) {
    onChange(active.filter((f) => f !== field));
  }

  function handleDragStart(index: number) {
    dragIndex.current = index;
  }

  function handleDragOver(e: React.DragEvent, index: number) {
    e.preventDefault();
    setDragOverIndex(index);
  }

  function handleDrop(e: React.DragEvent, dropIndex: number) {
    e.preventDefault();
    const from = dragIndex.current;
    if (from === null || from === dropIndex) {
      dragIndex.current = null;
      setDragOverIndex(null);
      return;
    }
    const next = [...active];
    const [moved] = next.splice(from, 1);
    next.splice(dropIndex, 0, moved);
    dragIndex.current = null;
    setDragOverIndex(null);
    onChange(next);
  }

  function handleDragEnd() {
    dragIndex.current = null;
    setDragOverIndex(null);
  }

  if (active.length === 0 && available.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-bg-surface px-3 py-2 text-sm">
      <span className="shrink-0 text-xs font-medium uppercase tracking-wide text-text-secondary">
        Group by
      </span>

      {/* Active chips (draggable, ordered) */}
      {active.map((field, i) => (
        // biome-ignore lint/a11y/noStaticElementInteractions: Draggable element
        <div
          key={field}
          draggable
          onDragStart={() => handleDragStart(i)}
          onDragOver={(e) => handleDragOver(e, i)}
          onDrop={(e) => handleDrop(e, i)}
          onDragEnd={handleDragEnd}
          className={`flex cursor-grab items-center gap-1 rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent transition-opacity active:cursor-grabbing ${
            dragOverIndex === i && dragIndex.current !== i ? 'opacity-50' : 'opacity-100'
          }`}
        >
          {/* drag handle */}
          <span className="select-none text-accent/50" aria-hidden="true">
            ⠿
          </span>
          <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-accent text-[9px] font-bold text-white">
            {i + 1}
          </span>
          <span>{FIELD_LABELS[field]}</span>
          <button
            type="button"
            onClick={() => removeField(field)}
            className="ml-0.5 rounded-full p-0.5 text-accent/60 hover:bg-accent/20 hover:text-accent focus:outline-none"
            aria-label={`Remove ${FIELD_LABELS[field]} grouping`}
          >
            <svg viewBox="0 0 10 10" className="h-2.5 w-2.5" fill="currentColor" aria-hidden="true">
              <path
                d="M1 1l8 8M9 1L1 9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      ))}

      {/* Separator */}
      {active.length > 0 && available.length > 0 && (
        <span className="text-border" aria-hidden="true">
          |
        </span>
      )}

      {/* Available chips (click to add) */}
      {available.map((field) => (
        <button
          key={field}
          type="button"
          onClick={() => addField(field)}
          className="flex items-center gap-1 rounded-full border border-border bg-bg-primary px-2 py-0.5 text-xs font-medium text-text-secondary transition-colors hover:border-accent/40 hover:bg-accent/5 hover:text-accent focus:outline-none focus:ring-2 focus:ring-accent"
        >
          <svg viewBox="0 0 10 10" className="h-2.5 w-2.5" fill="currentColor" aria-hidden="true">
            <path d="M5 1v8M1 5h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          {FIELD_LABELS[field]}
        </button>
      ))}

      {/* Clear all */}
      {active.length > 0 && (
        <button
          type="button"
          onClick={() => onChange([])}
          className="ml-auto text-xs text-text-secondary underline-offset-2 hover:text-text-primary hover:underline focus:outline-none"
        >
          Clear all
        </button>
      )}
    </div>
  );
}
